import { mergeCallbackOptionsWithPick } from "@mirage-web/utils/merge-callbacks";
import {
  DefaultError,
  InfiniteData,
  infiniteQueryOptions,
  InvalidateOptions,
  InvalidateQueryFilters,
  QueryClient,
  QueryKey,
  skipToken,
  Updater,
  useInfiniteQuery,
  UseInfiniteQueryOptions,
} from "@tanstack/react-query";
import { z } from "zod";

import { NetworkingLayerConfig } from "../../config";
import { extractVariables, makePaginatedQueryFetcher } from "../../default-fetcher";
import { mergeQueryKeysWithParams } from "../query-key";
import {
  CallerCombinedVariablesForPagination,
  ExtractPaginatedItemTypeWithKey,
  KeysWithPaginatedItems,
  QueryDefinitionOptions,
  QueryFetcherOptions,
  QueryPaginationParams,
} from "../types";

interface QueryPaginationDefinitionOptions<
  ParamsSchema extends z.ZodTypeAny,
  ParamsForPagination extends Partial<z.infer<ParamsSchema>>,
  TransformedResult,
  PageParam,
> {
  getNextPageParam: (
    lastPage: TransformedResult,
    allPages: TransformedResult[],
    lastPageParam: PageParam,
    allPageParams: PageParam[]
  ) => PageParam;

  initialPageParam?: PageParam;

  mapPaginationParamsToQueryParams: (
    paginationParams: QueryPaginationParams<PageParam>
  ) => ParamsForPagination;
}

interface CustomUseInfiniteQueryCallerOptions<
  TQueryKey extends QueryKey,
  TransformedResult,
  PageParam,
>
  extends
    Omit<QueryFetcherOptions<TransformedResult>, "onSuccess" | "onError">,
    Omit<
      UseInfiniteQueryOptions<
        TransformedResult,
        DefaultError,
        InfiniteData<TransformedResult, PageParam>,
        TQueryKey,
        PageParam
      >,
      "getNextPageParam" | "queryKey" | "initialPageParam" | "initialData"
    > {}

export function createQueryInfiniteMixin<
  TQueryKey extends QueryKey,
  Path extends string,
  ParamsSchema extends z.ZodTypeAny,
  ResponseSchema extends z.ZodTypeAny,
  TransformedResult extends object,
>(
  config: NetworkingLayerConfig,
  originalDefinitionOptions: QueryDefinitionOptions<
    TQueryKey,
    Path,
    ParamsSchema,
    ResponseSchema,
    TransformedResult
  >
) {
  return {
    withPagination<ParamsForPagination extends Partial<z.infer<ParamsSchema>>, PageParam>(
      paginationOptions: QueryPaginationDefinitionOptions<
        ParamsSchema,
        ParamsForPagination,
        TransformedResult,
        PageParam
      >
    ) {
      function makeFetcher(
        variables:
          | CallerCombinedVariablesForPagination<Path, ParamsSchema, ParamsForPagination>
          | Record<string, never>,
        callerOptions: QueryFetcherOptions<TransformedResult> = {}
      ) {
        const { onError, onSuccess } = mergeCallbackOptionsWithPick(
          ["onError", "onSuccess"],
          originalDefinitionOptions,
          callerOptions
        );

        return makePaginatedQueryFetcher(config, {
          ...originalDefinitionOptions,
          ...callerOptions,
          headers: { ...originalDefinitionOptions.headers, ...callerOptions.headers },
          onError,
          onSuccess,
          variables,
          mapPaginationParamsToQueryParams: paginationOptions.mapPaginationParamsToQueryParams,
        });
      }

      function queryOptions(
        combinedParams:
          | CallerCombinedVariablesForPagination<Path, ParamsSchema, ParamsForPagination>
          | typeof skipToken,
        callerOptions: CustomUseInfiniteQueryCallerOptions<
          TQueryKey,
          TransformedResult,
          PageParam
        > = {}
      ) {
        const variables = combinedParams === skipToken ? {} : combinedParams;

        return infiniteQueryOptions({
          queryKey: mergeQueryKeysWithParams(
            originalDefinitionOptions.queryKey,
            extractVariables(variables)
          ),
          queryFn: makeFetcher(variables, callerOptions),

          getNextPageParam: paginationOptions.getNextPageParam,
          initialPageParam: (paginationOptions.initialPageParam ?? null) as PageParam,

          enabled: combinedParams !== skipToken,
          ...callerOptions,
        });
      }

      function useInfiniteQueryHook(
        combinedParams:
          | CallerCombinedVariablesForPagination<Path, ParamsSchema, ParamsForPagination>
          | typeof skipToken,
        callerOptions: CustomUseInfiniteQueryCallerOptions<
          TQueryKey,
          TransformedResult,
          PageParam
        > = {}
      ) {
        return useInfiniteQuery(queryOptions(combinedParams, callerOptions));
      }

      async function invalidateQuery(
        queryClient: QueryClient,
        combinedParams:
          | CallerCombinedVariablesForPagination<Path, ParamsSchema, ParamsForPagination>
          | typeof skipToken,
        filters?: Omit<InvalidateQueryFilters, "queryKey" | "exact">,
        options?: InvalidateOptions
      ) {
        const { queryKey } = queryOptions(combinedParams);
        await queryClient.invalidateQueries({ queryKey, exact: true, ...filters }, options);
      }

      async function invalidateAllQueries(
        queryClient: QueryClient,
        filters?: Omit<InvalidateQueryFilters, "queryKey" | "exact">,
        options?: InvalidateOptions
      ) {
        await queryClient.invalidateQueries(
          { queryKey: originalDefinitionOptions.queryKey, exact: false, ...filters },
          options
        );
      }

      function setQueryData(
        queryClient: QueryClient,
        combinedParams: CallerCombinedVariablesForPagination<
          Path,
          ParamsSchema,
          ParamsForPagination
        >,
        updater: Updater<
          InfiniteData<TransformedResult> | undefined,
          InfiniteData<TransformedResult> | undefined
        >
      ) {
        const { queryKey } = queryOptions(combinedParams);
        queryClient.setQueryData<InfiniteData<TransformedResult>>(queryKey, updater);
      }

      function setAllQueriesData(
        queryClient: QueryClient,
        updater: Updater<
          InfiniteData<TransformedResult> | undefined,
          InfiniteData<TransformedResult> | undefined
        >
      ) {
        queryClient.setQueriesData<InfiniteData<TransformedResult>>(
          { queryKey: originalDefinitionOptions.queryKey },
          updater
        );
      }

      function updateItemsInQueryData<Key extends KeysWithPaginatedItems<TransformedResult>>(
        queryClient: QueryClient,
        combinedParams: CallerCombinedVariablesForPagination<
          Path,
          ParamsSchema,
          ParamsForPagination
        >,
        pageKey: Key,
        updateItem: (
          data: ExtractPaginatedItemTypeWithKey<TransformedResult, Key>
        ) => ExtractPaginatedItemTypeWithKey<TransformedResult, Key>
      ) {
        setQueryData(queryClient, combinedParams, (data) => {
          if (!data) {
            return data;
          }

          return {
            ...data,
            pages: data.pages.map((page) => ({
              ...page,
              [pageKey]: {
                ...page[pageKey],
                page: (
                  page[pageKey] as {
                    page: ExtractPaginatedItemTypeWithKey<TransformedResult, Key>[];
                  }
                ).page.map(updateItem),
              },
            })),
          };
        });
      }

      function findItemInQueryData<Key extends KeysWithPaginatedItems<TransformedResult>>(
        queryClient: QueryClient,
        combinedParams: CallerCombinedVariablesForPagination<
          Path,
          ParamsSchema,
          ParamsForPagination
        >,
        pageKey: Key,
        findItem: (data: ExtractPaginatedItemTypeWithKey<TransformedResult, Key>) => boolean
      ): ExtractPaginatedItemTypeWithKey<TransformedResult, Key> | undefined {
        const { queryKey } = queryOptions(combinedParams);
        const data = queryClient.getQueryData<InfiniteData<TransformedResult>>(queryKey);

        if (!data) {
          return;
        }

        return data.pages
          .flatMap(
            (page) =>
              (page[pageKey] as { page: ExtractPaginatedItemTypeWithKey<TransformedResult, Key>[] })
                .page
          )
          .find(findItem);
      }

      function insertItemInQueryData<Key extends KeysWithPaginatedItems<TransformedResult>>(
        queryClient: QueryClient,
        combinedParams: CallerCombinedVariablesForPagination<
          Path,
          ParamsSchema,
          ParamsForPagination
        >,
        pageKey: Key,
        newItem: ExtractPaginatedItemTypeWithKey<TransformedResult, Key>,
        position: "start" | "end"
      ) {
        setQueryData(queryClient, combinedParams, (data) => {
          if (!data) {
            return data;
          }

          const targetPageIdx = position === "start" ? 0 : data.pages.length - 1;
          return {
            ...data,
            pages: data.pages.map((page, pageIdx) => {
              if (pageIdx !== targetPageIdx) {
                return page;
              }
              const items = (
                page[pageKey] as { page: ExtractPaginatedItemTypeWithKey<TransformedResult, Key>[] }
              ).page;
              return {
                ...page,
                [pageKey]: {
                  ...page[pageKey],
                  page: position === "start" ? [newItem, ...items] : [...items, newItem],
                },
              };
            }),
          };
        });
      }

      function removeItemFromQueryData<Key extends KeysWithPaginatedItems<TransformedResult>>(
        queryClient: QueryClient,
        combinedParams: CallerCombinedVariablesForPagination<
          Path,
          ParamsSchema,
          ParamsForPagination
        >,
        pageKey: Key,
        matchItem: (item: ExtractPaginatedItemTypeWithKey<TransformedResult, Key>) => boolean
      ) {
        setQueryData(queryClient, combinedParams, (data) => {
          if (!data) {
            return data;
          }

          return {
            ...data,
            pages: data.pages.map((page) => ({
              ...page,
              [pageKey]: {
                ...page[pageKey],
                page: (
                  page[pageKey] as {
                    page: ExtractPaginatedItemTypeWithKey<TransformedResult, Key>[];
                  }
                ).page.filter((item) => !matchItem(item)),
              },
            })),
          };
        });
      }

      return {
        queryOptions,
        useInfiniteQuery: useInfiniteQueryHook,
        invalidateQuery,
        invalidateAllQueries,
        setQueryData,
        setAllQueriesData,
        updateItemsInQueryData,
        findItemInQueryData,
        removeItemFromQueryData,
        insertItemInQueryData,
      };
    },
  };
}
